/**
 * Jaipur Handloom reusable storefront module
 * File: jh-80-recommendations.js
 * Purpose: Recommendation rails, bundles, recently viewed history, quick-add integration and review anchors.
 * Dependencies: Shopify storefront markup and window.JH utilities.
 * Contract: Preserve existing classes, data-jh-* attributes, setting IDs and public events.
 * Maintenance: Extend this owner instead of appending a later patch or replacement controller.
 */
/* Recommendation owner. Delegates all cart writes to jh-90-cart.js and reads Shopify recommendation endpoints. */
(function() {
  'use strict';

  window.JH = window.JH || {};

  if (window.JH.Recommendations && window.JH.Recommendations.__bootstrapped) {
    if (typeof window.JH.Recommendations.refresh === 'function') {
      window.JH.Recommendations.refresh();
    }
    return;
  }

  var STORAGE = {
    products: 'jh_recent_products',
    collections: 'jh_recent_collections',
    searches: 'jh_recent_search_terms',
    cartIntent: 'jh_cart_intent',
    lastProductType: 'jh_last_product_type'
  };

  var state = {
    listenersBound: false,
    variantsByProductId: {},
    sectionInitCount: 0,
    collectionProductCache: {},
    recentProductCache: {}
  };

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function money(cents) {
    cents = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    var currency = (window.JHRecommendationsSettings && window.JHRecommendationsSettings.currency) || '';
    return currency + (cents / 100).toFixed(2);
  }

  function readJSON(key, fallback) {
    try {
      var value = window.localStorage ? localStorage.getItem(key) : null;
      return value ? JSON.parse(value) : fallback;
    } catch (err) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      if (window.localStorage) localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {}
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>'"]/g, function(char) {
      return ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      })[char];
    });
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/`/g, '&#96;');
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function uniqueByHandle(items) {
    var seen = {};
    return (items || []).filter(function(item) {
      if (!item || !item.handle || seen[item.handle]) return false;
      seen[item.handle] = true;
      return true;
    });
  }

  function inferIntent(text) {
    text = String(text || '').toLowerCase();
    if (/doormat|half[\s-]?moon|entry|entrance|foyer|door mat/.test(text)) return 'entryway';
    if (/runner|hallway|corridor|kitchen/.test(text)) return 'runner';
    if (/kantha|quilt|throw|blanket|bedroom|bed cover|bedspread/.test(text)) return 'bedroom';
    if (/pillow|cushion/.test(text)) return 'cushion';
    if (/pouf|ottoman/.test(text)) return 'pouf';
    if (/rug|dhurrie|jute|cotton|wool|hemp|carpet|area rug|round rug|scallop/.test(text)) return 'rug';
    return 'general';
  }

  function parseVariantJSONForProduct(el) {
    if (!el) return;
    var productId = el.getAttribute('data-product-id');
    var scriptId = el.getAttribute('data-product-variants-json-id');
    if (!productId || !scriptId || state.variantsByProductId[productId]) return;
    var script = document.getElementById(scriptId);
    if (!script) return;
    try {
      var variants = JSON.parse(script.textContent || '[]');
      var map = {};
      variants.forEach(function(variant) {
        map[String(variant.id)] = variant;
      });
      state.variantsByProductId[productId] = map;
    } catch (err) {}
  }

  function getCurrentProductEl() {
    return document.querySelector('[data-jh-current-product]');
  }

  function getCurrentVariant(productEl, variantId) {
    if (!productEl || !variantId) return null;
    parseVariantJSONForProduct(productEl);
    var productId = productEl.getAttribute('data-product-id');
    return state.variantsByProductId[productId] && state.variantsByProductId[productId][String(variantId)];
  }

  function storeViewedProduct() {
    var el = getCurrentProductEl();
    if (!el) return;
    parseVariantJSONForProduct(el);

    var title = el.getAttribute('data-product-title') || '';
    var type = el.getAttribute('data-product-type') || '';
    var tags = el.getAttribute('data-product-tags') || '';
    var productId = el.getAttribute('data-product-id');
    var variantMap = state.variantsByProductId[productId] || {};
    var storedVariants = Object.keys(variantMap).map(function(key) {
      var variant = variantMap[key] || {};
      return {
        id: variant.id,
        title: variant.title || variant.name || 'Option',
        price: Number(variant.price || 0),
        compare_at_price: Number(variant.compare_at_price || 0),
        available: variant.available !== false
      };
    }).filter(function(variant) {
      return variant.id && variant.available;
    }).slice(0, 80);
    var product = {
      id: productId,
      handle: el.getAttribute('data-product-handle'),
      title: title,
      url: el.getAttribute('data-product-url'),
      image: el.getAttribute('data-product-image'),
      price: Number(el.getAttribute('data-product-price') || 0),
      compare_at_price: Number(el.getAttribute('data-product-compare-price') || 0),
      variant_id: el.getAttribute('data-variant-id'),
      variant_count: Number(el.getAttribute('data-product-variant-count') || storedVariants.length || 1),
      variants: storedVariants,
      available: el.getAttribute('data-product-available') !== 'false',
      type: type,
      tags: tags,
      intent: inferIntent([title, type, tags].join(' ')),
      timestamp: Date.now()
    };

    if (!product.handle || !product.available) return;

    var items = readJSON(STORAGE.products, []);
    items = items.filter(function(item) {
      return item.handle !== product.handle;
    });
    items.unshift(product);
    writeJSON(STORAGE.products, items.slice(0, 12));
    writeJSON(STORAGE.lastProductType, product.intent);
  }

  function storeCollectionIntent() {
    var el = document.querySelector('[data-jh-current-collection]');
    if (!el) return;
    var collection = {
      handle: el.getAttribute('data-collection-handle'),
      title: el.getAttribute('data-collection-title'),
      url: el.getAttribute('data-collection-url'),
      intent: inferIntent([el.getAttribute('data-collection-title'), el.getAttribute('data-collection-handle')].join(' ')),
      timestamp: Date.now()
    };
    if (!collection.handle) return;
    var items = readJSON(STORAGE.collections, []);
    items = items.filter(function(item) {
      return item.handle !== collection.handle;
    });
    items.unshift(collection);
    writeJSON(STORAGE.collections, items.slice(0, 8));
  }

  function storeSearchIntent() {
    if (!/\/search/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q) return;
    var items = readJSON(STORAGE.searches, []);
    items = items.filter(function(item) {
      return item.term !== q;
    });
    items.unshift({
      term: q,
      intent: inferIntent(q),
      timestamp: Date.now()
    });
    writeJSON(STORAGE.searches, items.slice(0, 8));
  }

  function imageFromProduct(product) {
    if (!product) return '';
    if (product.image) return product.image;
    if (product.featured_image && typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.featured_media && product.featured_media.preview_image && product.featured_media.preview_image.src) return product.featured_media.preview_image.src;
    return '';
  }

  function fetchRecentProductByHandle(handle) {
    handle = String(handle || '');
    if (!handle) return Promise.resolve(null);
    if (state.recentProductCache[handle]) return state.recentProductCache[handle];

    var root = rootUrl();
    if (root.charAt(root.length - 1) !== '/') root += '/';
    var encoded = encodeURIComponent(handle);
    var urls = [
      root + 'products/' + encoded + '.js',
      '/products/' + encoded + '.js',
      root + 'products/' + encoded + '.json',
      '/products/' + encoded + '.json'
    ].filter(function(url, index, list) {
      return list.indexOf(url) === index;
    });

    function tryUrl(index) {
      if (index >= urls.length) return Promise.resolve(null);
      return fetch(urls[index], {
        cache: 'force-cache',
        headers: { 'Accept': 'application/json' }
      }).then(function(response) {
        if (!response.ok) throw new Error('Product request failed');
        return response.json();
      }).then(function(data) {
        var product = data && data.product ? data.product : data;
        if (!product || !product.variants || !product.variants.length) return tryUrl(index + 1);
        return product;
      }).catch(function() {
        return tryUrl(index + 1);
      });
    }

    state.recentProductCache[handle] = tryUrl(0).then(function(product) {
      if (!product) delete state.recentProductCache[handle];
      return product;
    });
    return state.recentProductCache[handle];
  }

  function mergeRecentProductData(stored, product) {
    if (!product || !product.variants || !product.variants.length) return stored;
    var variants = product.variants.map(function(variant) {
      return {
        id: variant.id,
        title: variant.title || variant.name || 'Option',
        price: Number(variant.price || 0),
        compare_at_price: Number(variant.compare_at_price || 0),
        available: variant.available !== false
      };
    });
    var selected = null;
    variants.forEach(function(variant) {
      if (!selected && String(variant.id) === String(stored.variant_id) && variant.available) selected = variant;
    });
    if (!selected) {
      selected = variants.filter(function(variant) { return variant.available; })[0] || variants[0];
    }
    return {
      id: product.id || stored.id,
      handle: product.handle || stored.handle,
      title: product.title || stored.title,
      url: product.url || stored.url || (stored.handle ? rootUrl() + 'products/' + stored.handle : '#'),
      image: imageFromProduct(product) || stored.image,
      price: Number(selected && selected.price || product.price || stored.price || 0),
      compare_at_price: Number(selected && selected.compare_at_price || product.compare_at_price || stored.compare_at_price || 0),
      variant_id: selected && selected.id || stored.variant_id,
      variant_count: variants.length,
      variants: variants,
      available: product.available !== false,
      type: product.type || product.product_type || stored.type || '',
      tags: Array.isArray(product.tags) ? product.tags.join(' ') : (product.tags || stored.tags || ''),
      intent: stored.intent || inferIntent([product.title, product.type, product.tags].join(' ')),
      timestamp: stored.timestamp || Date.now()
    };
  }

  function recentProductNeedsHydration(product) {
    if (!product || !product.handle) return false;
    if (!product.variants || !product.variants.length) return true;
    return Number(product.variant_count || product.variants.length) > product.variants.length;
  }

  function hydrateRecentlyViewedProducts(container, items) {
    var missing = (items || []).filter(recentProductNeedsHydration);
    if (!missing.length || container.getAttribute('data-jh-recent-hydrating') === 'true') return;
    container.setAttribute('data-jh-recent-hydrating', 'true');

    Promise.all(missing.map(function(item) {
      return fetchRecentProductByHandle(item.handle).then(function(product) {
        return { handle: item.handle, product: product };
      });
    })).then(function(results) {
      var byHandle = {};
      results.forEach(function(result) {
        if (result && result.handle && result.product) byHandle[result.handle] = result.product;
      });
      if (!Object.keys(byHandle).length) return;

      var storedItems = readJSON(STORAGE.products, []);
      var changed = false;
      storedItems = storedItems.map(function(item) {
        if (!byHandle[item.handle]) return item;
        changed = true;
        return mergeRecentProductData(item, byHandle[item.handle]);
      });
      if (changed) writeJSON(STORAGE.products, storedItems.slice(0, 12));
    }).finally(function() {
      if (document.documentElement.contains(container)) {
        container.removeAttribute('data-jh-recent-hydrating');
        var current = container.getAttribute('data-current-handle') || '';
        var max = Number(container.getAttribute('data-max') || 8);
        var refreshed = uniqueByHandle(readJSON(STORAGE.products, []))
          .filter(function(item) {
            return item.handle && item.handle !== current && item.available !== false;
          })
          .slice(0, max);
        if (refreshed.length) container.innerHTML = refreshed.map(cardFromProduct).join('');
      }
    });
  }

  function firstVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i += 1) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  function hasMultipleVariants(product) {
    if (!product) return false;
    if (typeof product.variant_count !== 'undefined') return Number(product.variant_count) > 1;
    return product.variants && product.variants.length > 1;
  }

  function variantSelectHtml(product, selectedVariant, className, dataAttr) {
    var variants = product && product.variants || [];
    if (variants.length <= 1) return '';
    var options = variants.filter(function(v) { return v && v.available; }).map(function(v) {
      var label = v.title || v.name || 'Option';
      if (Number(v.price || 0) !== Number(selectedVariant && selectedVariant.price || 0)) label += ' · ' + money(v.price || 0);
      return '<option value="' + escapeAttr(v.id) + '" data-price="' + escapeAttr(v.price || 0) + '" data-compare-price="' + escapeAttr(v.compare_at_price || 0) + '"' + (selectedVariant && String(v.id) === String(selectedVariant.id) ? ' selected' : '') + '>' + escapeHtml(label) + '</option>';
    }).join('');
    if (!options) return '';
    return '<label class="' + className + '"><span>Choose size</span><select ' + dataAttr + ' aria-label="Choose size for ' + escapeAttr(product.title || 'product') + '">' + options + '</select></label>';
  }

  function cardFromProduct(product) {
    var variant = null;
    if (product && product.variant_id && product.variants) {
      for (var vi = 0; vi < product.variants.length; vi += 1) {
        if (String(product.variants[vi].id) === String(product.variant_id) && product.variants[vi].available) {
          variant = product.variants[vi];
          break;
        }
      }
    }
    variant = variant || firstVariant(product) || {};
    var image = imageFromProduct(product);
    var title = product.title || '';
    var handle = product.handle || '';
    var url = product.url || (handle ? (rootUrl() + 'products/' + handle) : '#');
    var price = Number(variant.price || product.price || 0);
    var compare = Number(variant.compare_at_price || product.compare_at_price || product.compare_at_price_max || 0);
    var variantId = variant.id || product.variant_id || '';
    var available = product.available !== false && (!product.variants || product.variants.some(function(v) {
      return v.available;
    }));
    var hasVariantData = product.variants && product.variants.length;
    var selector = hasVariantData ? variantSelectHtml(product, variant, 'jh-rec-variant-picker', 'data-jh-rec-variant-select') :
      '<label class="jh-rec-variant-picker" data-jh-recent-variant-loading><span>Choose size</span><select disabled aria-busy="true" aria-label="Loading sizes for ' + escapeAttr(title) + '"><option>Loading sizes…</option></select></label>';
    var button = variantId && available && hasVariantData ?
      '<button class="jh-rec-card__button" type="button" data-jh-quick-add data-variant-id="' + escapeAttr(variantId) + '">Add to cart</button>' :
      '<button class="jh-rec-card__button" type="button" disabled aria-busy="true">Loading options…</button>';

    return [
      '<article class="jh-rec-card" data-product-handle="' + escapeAttr(handle) + '" data-product-id="' + escapeAttr(product.id || '') + '">',
      '<a class="jh-rec-card__media" href="' + escapeAttr(url) + '">',
      image ? '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(title) + '" loading="lazy">' : '',
      '</a>',
      '<div class="jh-rec-card__body">',
      '<a class="jh-rec-card__title" href="' + escapeAttr(url) + '">' + escapeHtml(title) + '</a>',
      '<p class="jh-rec-card__price" data-jh-rec-price>' + money(price) + (compare > price ? '<span class="jh-rec-card__compare">' + money(compare) + '</span>' : '') + '</p>',
      selector,
      button,
      '</div>',
      '</article>'
    ].join('');
  }

  function priceToCents(value) {
    if (typeof value === 'number') return Math.round(value);
    value = String(value == null ? '' : value).replace(/[^0-9.-]/g, '');
    if (!value) return 0;
    return value.indexOf('.') > -1 ? Math.round(parseFloat(value) * 100) : parseInt(value, 10) || 0;
  }

  function normalizeCollectionProduct(product) {
    if (!product) return null;
    product.url = product.url || (product.handle ? rootUrl() + 'products/' + product.handle : '#');
    if (product.featured_image && product.featured_image.src) product.image = product.featured_image.src;
    else if (typeof product.featured_image === 'string') product.image = product.featured_image;
    product.variants = (product.variants || []).map(function(variant) {
      variant.price = priceToCents(variant.price);
      variant.compare_at_price = priceToCents(variant.compare_at_price);
      return variant;
    });
    product.available = product.available !== false && product.variants.some(function(variant) { return variant.available !== false; });
    product.variant_count = product.variants.length;
    return product;
  }

  function collectionProducts(handle, limit) {
    handle = String(handle || '');
    if (!handle) return Promise.resolve([]);
    var key = handle + ':' + (limit || 12);
    if (state.collectionProductCache[key]) return state.collectionProductCache[key];
    var base = rootUrl();
    if (base.charAt(base.length - 1) !== '/') base += '/';
    var url = base + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=' + (limit || 12);
    state.collectionProductCache[key] = fetch(url, { credentials: 'same-origin', cache: 'force-cache' }).then(function(response) {
      if (!response.ok) return [];
      return response.json();
    }).then(function(payload) {
      return ((payload && payload.products) || []).map(normalizeCollectionProduct).filter(Boolean);
    }).catch(function() { return []; });
    return state.collectionProductCache[key];
  }

  function decorCategory(product) {
    var text = [product && product.title, product && product.product_type, product && product.type, product && product.handle, product && product.tags].join(' ').toLowerCase();
    if (/pillow|cushion|sham/.test(text)) return 'pillow';
    if (/pouf|ottoman/.test(text)) return 'pouf';
    if (/valance|curtain|window/.test(text)) return 'valance';
    if (/scarf|scarves|shawl/.test(text)) return 'scarf';
    if (/kantha|throw|quilt|blanket/.test(text)) return 'throw';
    return '';
  }

  function isDecorProduct(product) {
    var text = [product && product.title, product && product.product_type, product && product.type, product && product.handle, product && product.tags].join(' ').toLowerCase();
    if (/rug|runner|doormat|door mat|carpet|dhurrie|durrie/.test(text)) return false;
    return !!decorCategory(product);
  }

  function rotateProducts(items, seed) {
    items = items.slice();
    if (!items.length) return items;
    var offset = Math.abs(Number(seed || 0)) % items.length;
    return items.slice(offset).concat(items.slice(0, offset));
  }

  function hydrateCompleteLookRails() {
    var rails = toArray(document.querySelectorAll('[data-jh-complete-look-rail]'));
    if (!rails.length) return;
    var handleGroups = [
      ['cushion-covers', 'pillow-covers', 'pillows', 'pillows-cushions', 'kantha-pillows'],
      ['poufs', 'poufs-ottomans', 'ottomans', 'floor-poufs'],
      ['kantha-throws', 'throws', 'kantha-quilts', 'kantha-throws-quilts'],
      ['valances', 'kantha-valances', 'window-valances', 'valances-parasols'],
      ['kantha-scarves', 'scarves', 'kantha-scarf']
    ];

    function firstAvailableGroup(handles, index) {
      index = index || 0;
      if (!handles[index]) return Promise.resolve([]);
      return collectionProducts(handles[index], 8).then(function(products) {
        if (products && products.length) return products;
        return firstAvailableGroup(handles, index + 1);
      });
    }
    rails.forEach(function(rail) {
      if (rail.getAttribute('data-jh-complete-look-loading') === 'true') return;
      if (rail.children.length) {
        var existingSection = sectionFor(rail);
        if (existingSection) existingSection.classList.remove('jh-hidden');
        return;
      }
      rail.setAttribute('data-jh-complete-look-loading', 'true');
      var section = closest(rail, '[data-jh-complete-look-section]') || sectionFor(rail);
      var max = Math.max(5, Math.min(12, parseInt(section && section.getAttribute('data-max-products'), 10) || 8));
      var currentHandle = section && section.getAttribute('data-current-handle') || '';
      var productId = parseInt(section && section.getAttribute('data-product-id'), 10) || 0;
      var day = Math.floor(Date.now() / 86400000);
      Promise.all(handleGroups.map(function(handles) { return firstAvailableGroup(handles, 0); })).then(function(groups) {
        var pool = uniqueByHandle([].concat.apply([], groups)).filter(function(product) {
          return product && product.available && product.handle !== currentHandle && isDecorProduct(product);
        });
        if (!pool.length) return collectionProducts('all', 250).then(function(allProducts) {
          return allProducts.filter(function(product) {
            return product && product.available && product.handle !== currentHandle && isDecorProduct(product);
          });
        });
        return pool;
      }).then(function(pool) {
        pool = uniqueByHandle(pool || []);
        if (!pool.length) return;
        pool = rotateProducts(pool, productId + day);
        var chosen = [];
        var usedCategories = {};
        pool.forEach(function(product) {
          var category = decorCategory(product);
          if (category && !usedCategories[category] && chosen.length < max) {
            chosen.push(product);
            usedCategories[category] = true;
          }
        });
        pool.forEach(function(product) {
          if (chosen.length >= max || chosen.indexOf(product) !== -1) return;
          chosen.push(product);
        });
        rail.innerHTML = chosen.slice(0, max).map(cardFromProduct).join('');
        if (section) section.classList.toggle('jh-hidden', !rail.children.length);
      }).finally(function() {
        rail.removeAttribute('data-jh-complete-look-loading');
      });
    });
  }

  function sectionFor(el) {
    return closest(el, '.jh-rec-section') || closest(el, '.jh-cart-upsell') || closest(el, '.jh-added-popup');
  }

  function hookSelector(name) {
    return '[data-jh-hook="' + String(name || '').replace(/"/g, '\"') + '"]';
  }

  function ensureHook(name, target, where) {
    if (!name || document.querySelector(hookSelector(name))) return;
    if (!target || !target.parentNode) return;
    var hook = document.createElement('div');
    hook.className = 'jh-hook jh-hook--auto jh-hook--' + String(name).replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
    hook.setAttribute('data-jh-hook', name);
    hook.setAttribute('data-jh-auto-hook', 'true');
    if (where === 'after') target.parentNode.insertBefore(hook, target.nextSibling);
    else if (where === 'before') target.parentNode.insertBefore(hook, target);
    else target.appendChild(hook);
  }

  function createFallbackHooks() {
    var productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) ensureHook('product-after-form', productForm, 'after');

    var productDescription = document.querySelector('[data-jh-product-after-description], .jh-product-recommendations-after-description, .jh-accordion, .product__description, .product-description, [data-product-description]');
    if (productDescription && /products\//.test(window.location.pathname)) ensureHook('product-after-description', productDescription, productDescription.hasAttribute('data-jh-product-after-description') ? 'append' : 'after');

    var productTitle = document.querySelector('.product__title, .product-title, h1.product-title, h1');
    if (productTitle && /products\//.test(window.location.pathname)) ensureHook('product-under-title', productTitle, 'after');

    var productMedia = document.querySelector('.product__media-wrapper, .product-media, .product-gallery, [data-product-media-gallery]');
    if (productMedia) ensureHook('product-under-media', productMedia, 'append');

    var cartDrawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer, [id*="CartDrawer"], [class*="cart-drawer"]');
    if (cartDrawer) ensureHook('cart-drawer-bottom', cartDrawer, 'append');

    var cartForm = document.querySelector('form[action="/cart"], form[action*="/cart"], .cart-page, main .cart');
    if (cartForm && /\/cart/.test(window.location.pathname)) ensureHook('cart-page-after-items', cartForm, 'append');

    var collectionGrid = document.querySelector('#ProductGridContainer, .collection .product-grid, .collection-grid, [data-collection-products]');
    if (collectionGrid) ensureHook('collection-after-grid', collectionGrid, 'after');

    var main = document.querySelector('main, #MainContent, [role="main"]');
    var isHome = window.location.pathname === rootUrl() || window.location.pathname === '/' || /\/index\/?$/.test(window.location.pathname);
    if (main && isHome) {
      ensureHook('home-upsell', main, 'append');
      ensureHook('home-recently-viewed', main, 'append');
    }
  }

  function mountSlots() {
    createFallbackHooks();
    toArray(document.querySelectorAll('[data-jh-slot]')).forEach(function(slot) {
      var mount = slot.getAttribute('data-jh-mount') || '';
      if (!mount || mount === 'none') return;
      var hook = document.querySelector(hookSelector(mount));
      if (!hook || hook.contains(slot)) return;
      hook.appendChild(slot);
      slot.setAttribute('data-jh-mounted', mount);
    });
  }

  function disableExistingUpsellSections() {
    var settings = window.JHRecommendationsSettings || {};
    var selectors = settings.disableExistingUpsellSelectors || [];
    if (!selectors.length) return;
    selectors.forEach(function(selector) {
      try {
        toArray(document.querySelectorAll(selector)).forEach(function(el) {
          if (el.closest('.jh-rec-section, .jh-cart-upsell, .jh-added-popup') || el.hasAttribute('data-jh-hook')) return;
          el.setAttribute('data-jh-disabled-existing-upsell', 'true');
          el.classList.add('jh-existing-upsell-hidden');
        });
      } catch (err) {}
    });
  }

  function setSectionVisibility(container, visible) {
    var section = sectionFor(container);
    if (section) section.classList.toggle('jh-hidden', !visible);
  }

  function renderRecentlyViewed() {
    toArray(document.querySelectorAll('[data-jh-recently-viewed]')).forEach(function(container) {
      var current = container.getAttribute('data-current-handle') || '';
      var max = Number(container.getAttribute('data-max') || 8);
      var items = uniqueByHandle(readJSON(STORAGE.products, []))
        .filter(function(item) {
          return item.handle && item.handle !== current && item.available !== false;
        })
        .slice(0, max);

      if (!items.length) {
        container.innerHTML = '';
        setSectionVisibility(container, false);
        return;
      }

      container.innerHTML = items.map(cardFromProduct).join('');
      setSectionVisibility(container, true);
      hydrateRecentlyViewedProducts(container, items);
    });
  }

  function getRenderedProductCount(container) {
    return container.querySelectorAll('[data-product-handle]:not(.jh-hidden):not([data-jh-hidden-by-cart="true"])').length;
  }

  function fetchProductRecommendations() {
    toArray(document.querySelectorAll('[data-jh-auto-recommendations]')).forEach(function(container) {
      var productId = container.getAttribute('data-product-id');
      var intent = container.getAttribute('data-intent') || 'related';
      var limit = Number(container.getAttribute('data-limit') || 4);
      var currentHandle = container.getAttribute('data-current-handle') || '';
      if (!productId || container.getAttribute('data-loaded') === 'true') return;

      var alreadyCount = getRenderedProductCount(container);
      var remainingLimit = Math.max(0, limit - alreadyCount);
      if (!remainingLimit) {
        container.setAttribute('data-loaded', 'true');
        setSectionVisibility(container, alreadyCount > 0);
        return;
      }

      var apiIntent = intent === 'complementary' ? 'complementary' : 'related';
      var url = rootUrl() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + encodeURIComponent(remainingLimit) + '&intent=' + encodeURIComponent(apiIntent);

      fetch(url, {
          headers: {
            'Accept': 'application/json'
          }
        })
        .then(function(res) {
          return res.ok ? res.json() : null;
        })
        .then(function(data) {
          var existingHandles = toArray(container.querySelectorAll('[data-product-handle]')).map(function(el) {
            return el.getAttribute('data-product-handle');
          });
          var products = data && data.products ? data.products.filter(function(p) {
            return p && p.available !== false && p.handle !== currentHandle && existingHandles.indexOf(p.handle) === -1;
          }).slice(0, remainingLimit) : [];
          if (products.length) {
            container.insertAdjacentHTML('beforeend', products.map(cardFromProduct).join(''));
          }
          container.setAttribute('data-loaded', 'true');
          setSectionVisibility(container, getRenderedProductCount(container) > 0);
        })
        .catch(function() {
          container.setAttribute('data-loaded', 'true');
          setSectionVisibility(container, getRenderedProductCount(container) > 0);
        });
    });
  }

  function getCart() {
    var owner = window.JH && window.JH.Cart;
    if (owner && typeof owner.get === 'function') return owner.get();
    if (window.JHSmartCart && typeof window.JHSmartCart.get === 'function') return window.JHSmartCart.get();
    return Promise.reject(new Error('Cart owner is not available'));
  }

  function updateCartCount(cart) {
    var selectors = ['[data-cart-count]', '[data-jh-cart-count]', '[data-jh-mini-cart-count]', '.cart-count-bubble span[aria-hidden="true"]', '.cart-count', '#CartCount'];
    selectors.forEach(function(selector) {
      toArray(document.querySelectorAll(selector)).forEach(function(el) {
        el.textContent = cart.item_count;
      });
    });
  }

  function cartText(cart) {
    return (cart.items || []).map(function(item) {
      return [item.product_title, item.product_type, item.variant_title, item.handle, item.url].join(' ');
    }).join(' ');
  }

  function updateCartIntent(cart) {
    var intent = inferIntent(cartText(cart));
    writeJSON(STORAGE.cartIntent, intent);
    return intent;
  }

  function updateProgressBars(cart) {
    toArray(document.querySelectorAll('[data-jh-progress]')).forEach(function(el) {
      var threshold = Number(el.getAttribute('data-threshold-cents') || 0);
      var successText = el.getAttribute('data-success-text') || 'You unlocked this offer.';
      var remainingText = el.getAttribute('data-remaining-text') || 'You are {{ amount }} away from this offer.';
      var amount = Number(cart.total_price || 0);
      if (!threshold) return;

      var progress = Math.max(0, Math.min(100, Math.round((amount / threshold) * 100)));
      var remaining = Math.max(0, threshold - amount);
      var message = remaining <= 0 ? successText : remainingText.replace('{{ amount }}', money(remaining));
      el.style.setProperty('--jh-progress', progress + '%');
      var msg = el.querySelector('[data-jh-progress-message]');
      var track = el.querySelector('[role="progressbar"]');
      if (msg) msg.textContent = message;
      if (track) track.setAttribute('aria-valuenow', String(progress));
    });
  }

  function cartHandles(cart) {
    var map = {};
    (cart.items || []).forEach(function(item) {
      if (item.handle) map[item.handle] = true;
      if (item.url) {
        var match = item.url.match(/\/products\/([^?/#]+)/);
        if (match && match[1]) map[match[1]] = true;
      }
    });
    return map;
  }

  function hideProductsAlreadyInCart(cart) {
    var handles = cartHandles(cart);
    toArray(document.querySelectorAll('[data-product-handle]')).forEach(function(el) {
      var handle = el.getAttribute('data-product-handle');
      if (!handle) return;
      var shouldHide = !!handles[handle] && el.getAttribute('data-jh-allow-current') !== 'true';
      el.classList.toggle('jh-hidden', shouldHide);
      if (shouldHide) el.setAttribute('data-jh-hidden-by-cart', 'true');
      else el.removeAttribute('data-jh-hidden-by-cart');
    });
  }

  function applyCartIntentGroups(cart) {
    var intent = updateCartIntent(cart);
    toArray(document.querySelectorAll('[data-jh-cart-upsell-groups]')).forEach(function(wrapper) {
      if (!cart || !cart.item_count) {
        toArray(wrapper.querySelectorAll('[data-jh-cart-intent-group]')).forEach(function(group) {
          group.classList.add('jh-hidden');
        });
        setSectionVisibility(wrapper, false);
        return;
      }
      var groups = toArray(wrapper.querySelectorAll('[data-jh-cart-intent-group]'));
      if (!groups.length) return;
      var chosen = null;
      groups.forEach(function(group) {
        var groupIntent = group.getAttribute('data-intent') || 'general';
        if (groupIntent === intent && getRenderedProductCount(group) > 0) chosen = group;
      });
      if (!chosen) {
        groups.forEach(function(group) {
          if (!chosen && group.getAttribute('data-intent') === 'general' && getRenderedProductCount(group) > 0) chosen = group;
        });
      }
      groups.forEach(function(group) {
        group.classList.toggle('jh-hidden', group !== chosen);
      });
      setSectionVisibility(wrapper, !!chosen);
    });
  }

  function refreshCartDependentUI(cart) {
    updateCartCount(cart);
    updateProgressBars(cart);
    hideProductsAlreadyInCart(cart);
    applyCartIntentGroups(cart);
  }

  function addItems(items) {
    var owner = window.JH && window.JH.Cart;
    var add = owner && owner.addItems;
    if (typeof add !== 'function' && window.JHSmartCart) add = window.JHSmartCart.addItems;
    if (typeof add !== 'function') return Promise.reject(new Error('Cart owner is not available'));

    return add(items, { openDrawer: false }).then(function(cart) {
      refreshCartDependentUI(cart);
      showAddedPopup();
      return cart;
    });
  }

  function buttonErrorMessage(err) {
    if (!err) return 'Unable to add';
    return err.description || err.message || 'Unable to add';
  }

  function handleQuickAdd(event) {
    var button = closest(event.target, '[data-jh-quick-add]');
    if (!button) return;
    event.preventDefault();
    var card = closest(button, '.jh-rec-card');
    var selector = card && card.querySelector('[data-jh-rec-variant-select]');
    var id = selector && selector.value ? selector.value : button.getAttribute('data-variant-id');
    if (!id || button.disabled) return;
    button.setAttribute('data-variant-id', id);
    button.disabled = true;
    var oldText = button.textContent;
    button.textContent = 'Adding...';
    addItems([{
        id: Number(id),
        quantity: 1
      }])
      .then(function() {
        button.textContent = 'Added';
      })
      .catch(function(err) {
        button.textContent = buttonErrorMessage(err);
      })
      .finally(function() {
        window.setTimeout(function() {
          button.disabled = false;
          button.textContent = oldText;
        }, 1800);
      });
  }

  function selectedBundleItems(bundle) {
    return toArray(bundle.querySelectorAll('[data-jh-bundle-checkbox]:checked')).filter(function(input) {
      var line = closest(input, '[data-product-handle]');
      return !(line && (line.classList.contains('jh-hidden') || line.getAttribute('data-jh-hidden-by-cart') === 'true'));
    }).map(function(input) {
      var id = Number(input.getAttribute('data-variant-id'));
      var quantity = Number(input.getAttribute('data-quantity') || 1);
      var bundleName = input.getAttribute('data-bundle-name') || 'Jaipur Handloom Bundle';
      var item = {
        id: id,
        quantity: quantity
      };
      if (input.getAttribute('data-jh-add-bundle-property') === 'true') {
        item.properties = {
          '_JH Bundle': bundleName
        };
      }
      return item;
    }).filter(function(item) {
      return item.id;
    });
  }

  function syncBundleSelectionButtons(scope) {
    toArray((scope || document).querySelectorAll('[data-jh-bundle-remove]')).forEach(function(button) {
      var line = closest(button, '.jh-bundle-line');
      var input = line && line.querySelector('[data-jh-bundle-checkbox]');
      if (!input) return;
      var selected = !!input.checked;
      button.textContent = selected ? 'Remove' : 'Select';
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.classList.toggle('is-select-action', !selected);
      var productTitle = line.querySelector('.jh-bundle-line__title');
      button.setAttribute('aria-label', (selected ? 'Remove ' : 'Select ') + (productTitle ? productTitle.textContent.trim() : 'item') + (selected ? ' from bundle selection' : ' for bundle selection'));
    });
  }

  function handleBundleSelection(event) {
    var button = closest(event.target, '[data-jh-bundle-remove]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    var line = closest(button, '.jh-bundle-line');
    var input = line && line.querySelector('[data-jh-bundle-checkbox]');
    if (!input || input.disabled) return;
    input.checked = !input.checked;
    syncBundleSelectionButtons(line || document);
    recalcBundleTotals();
  }

  function handleBundleAdd(event) {
    var button = closest(event.target, '[data-jh-add-bundle]');
    if (!button) return;
    event.preventDefault();
    var bundle = closest(button, '[data-jh-bundle]');
    if (!bundle || button.disabled) return;
    var items = selectedBundleItems(bundle);
    if (!items.length) return;
    button.disabled = true;
    var oldText = button.textContent;
    button.textContent = 'Adding bundle...';
    addItems(items)
      .then(function() {
        button.textContent = 'Bundle added';
      })
      .catch(function(err) {
        button.textContent = buttonErrorMessage(err);
      })
      .finally(function() {
        window.setTimeout(function() {
          button.disabled = false;
          button.textContent = oldText;
        }, 1800);
      });
  }

  function recalcBundleTotals() {
    syncBundleSelectionButtons(document);
    toArray(document.querySelectorAll('[data-jh-bundle]')).forEach(function(bundle) {
      var total = 0;
      var count = 0;
      toArray(bundle.querySelectorAll('[data-jh-bundle-checkbox]:checked')).forEach(function(input) {
        var line = closest(input, '[data-product-handle]');
        if (line && (line.classList.contains('jh-hidden') || line.getAttribute('data-jh-hidden-by-cart') === 'true')) return;
        total += Number(input.getAttribute('data-price') || 0) * Number(input.getAttribute('data-quantity') || 1);
        count += 1;
      });
      var totalEl = bundle.querySelector('[data-jh-bundle-total]');
      var countEl = bundle.querySelector('[data-jh-bundle-count]');
      var button = bundle.querySelector('[data-jh-add-bundle]');
      if (totalEl) totalEl.textContent = money(total);
      if (countEl) countEl.textContent = count;
      if (button) button.disabled = count === 0;
    });
  }

  function setCurrentVariant(id, variant) {
    var currentProduct = getCurrentProductEl();
    if (!currentProduct || !id) return;
    variant = variant || getCurrentVariant(currentProduct, id);
    currentProduct.setAttribute('data-variant-id', id);
    if (variant) {
      currentProduct.setAttribute('data-product-price', variant.price || 0);
      currentProduct.setAttribute('data-product-compare-price', variant.compare_at_price || 0);
    }
    toArray(document.querySelectorAll('[data-jh-current-variant-checkbox]')).forEach(function(input) {
      input.setAttribute('data-variant-id', id);
      if (variant) input.setAttribute('data-price', variant.price || 0);
      var line = closest(input, '.jh-bundle-line');
      var select = line && line.querySelector('[data-jh-bundle-variant-select]');
      if (select && Array.prototype.some.call(select.options, function(option) { return String(option.value) === String(id); })) select.value = String(id);
      var priceEl = line && line.querySelector('.jh-bundle-line__price');
      if (priceEl && variant) priceEl.textContent = money(variant.price || 0);
    });
    recalcBundleTotals();
    storeViewedProduct();
  }

  function syncCurrentVariant() {
    var currentProduct = getCurrentProductEl();
    if (!currentProduct) return;
    parseVariantJSONForProduct(currentProduct);
  }

  function updateRecommendationVariant(select) {
    if (!select || !select.value) return;
    var card = closest(select, '.jh-rec-card, .jh-smart-rec-card');
    if (!card) return;
    var option = select.options[select.selectedIndex];
    var button = card.querySelector('[data-jh-quick-add], [data-jh-smart-add]');
    if (button) button.setAttribute('data-variant-id', select.value);
    var price = Number(option && option.getAttribute('data-price') || 0);
    var compare = Number(option && option.getAttribute('data-compare-price') || 0);
    var priceEl = card.querySelector('[data-jh-rec-price], [data-jh-smart-rec-price]');
    if (priceEl && price) priceEl.innerHTML = money(price) + (compare > price ? '<span class="jh-rec-card__compare">' + money(compare) + '</span>' : '');
  }

  function updateBundleVariant(select) {
    if (!select || !select.value) return;
    var line = closest(select, '.jh-bundle-line');
    if (!line) return;
    var input = line.querySelector('[data-jh-bundle-checkbox]');
    var option = select.options[select.selectedIndex];
    var price = Number(option && option.getAttribute('data-price') || 0);
    if (input) {
      input.setAttribute('data-variant-id', select.value);
      if (price) input.setAttribute('data-price', price);
    }
    var priceEl = line.querySelector('.jh-bundle-line__price');
    if (priceEl && price) priceEl.textContent = money(price);
    recalcBundleTotals();
  }

  function handleVariantChange(event) {
    var target = event.target;
    if (!target) return;
    if (target.matches('[data-jh-rec-variant-select], [data-jh-smart-rec-variant-select]')) {
      updateRecommendationVariant(target);
      return;
    }
    if (target.matches('[data-jh-bundle-variant-select]')) {
      updateBundleVariant(target);
      return;
    }
    if (target.name === 'id' && target.value && !closest(target, '.jh-card-form')) setCurrentVariant(target.value);
  }

  function handleThemeVariantChange(event) {
    var variant = event.detail && (event.detail.variant || event.detail);
    if (variant && variant.id) setCurrentVariant(variant.id, variant);
  }

  function showAddedPopup() {
    var popup = document.querySelector('[data-jh-added-popup]');
    if (!popup) return;
    popup.classList.add('is-visible');
    popup.setAttribute('aria-hidden', 'false');
    clearTimeout(showAddedPopup.timer);
    showAddedPopup.timer = window.setTimeout(function() {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
    }, 5200);
  }

  function handlePopupClose(event) {
    var close = closest(event.target, '[data-jh-close-popup]');
    if (!close) return;
    event.preventDefault();
    var popup = closest(close, '[data-jh-added-popup]');
    if (popup) {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
    }
  }

  function scrollToReviews(event) {
    var link = closest(event.target, '[data-jh-review-scroll], [data-jh-review-summary-link]');
    if (!link) return;
    var targetSelectors = [
      '#judgeme_product_reviews',
      '#JHReviews',
      '#shopify-product-reviews',
      '#reviews',
      '.jdgm-rev-widg',
      '[data-judgeme-product-reviews]'
    ];
    var target = null;
    for (var i = 0; i < targetSelectors.length; i += 1) {
      target = document.querySelector(targetSelectors[i]);
      if (target) break;
    }
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    window.setTimeout(function() {
      var focusTarget = document.getElementById('JHReviews');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }, 450);
  }

  function updateReviewAnchors() {
    var anchors = toArray(document.querySelectorAll('[data-jh-review-anchor]'));
    if (!anchors.length) return;

    var text = '';
    var judgeBadge = document.querySelector('.jdgm-prev-badge__text');
    var summaryText = document.querySelector('.jdgm-rev-widg__summary-text');
    if (judgeBadge && judgeBadge.textContent.trim()) text = judgeBadge.textContent.trim();
    else if (summaryText && summaryText.textContent.trim()) text = summaryText.textContent.trim();

    if (text) {
      anchors.forEach(function(anchor) {
        var label = anchor.querySelector('[data-jh-review-label]');
        if (label) label.textContent = '★★★★★ ' + text.replace(/^★★★★★\s*/i, '');
      });
    }
  }

  function observeReviewChanges() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function() {
      updateReviewAnchors();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    window.setTimeout(function() {
      observer.disconnect();
    }, 10000);
  }

  function initCartProgress() {
    if (!document.querySelector('[data-jh-progress], [data-jh-cart-upsell-groups]')) return;
    getCart().then(refreshCartDependentUI).catch(function() {});
  }

  function bindListenersOnce() {
    if (state.listenersBound) return;
    state.listenersBound = true;
    document.addEventListener('click', handleQuickAdd);
    document.addEventListener('click', handleBundleAdd);
    document.addEventListener('click', handleBundleSelection);
    document.addEventListener('click', handlePopupClose);
    document.addEventListener('click', scrollToReviews);
    document.addEventListener('change', function(event) {
      if (event.target && event.target.matches('[data-jh-bundle-checkbox]')) { syncBundleSelectionButtons(closest(event.target, '.jh-bundle-line') || document); recalcBundleTotals(); }
      handleVariantChange(event);
    });
    document.addEventListener('variant:change', handleThemeVariantChange);
    document.addEventListener('shopify:section:load', function() {
      refresh();
    });
    document.addEventListener('jh:cart:changed', function(event) {
      if (event.detail) refreshCartDependentUI(event.detail);
    });
    window.setTimeout(updateReviewAnchors, 1200);
    window.setTimeout(updateReviewAnchors, 3000);
    observeReviewChanges();
  }

  function refresh() {
    state.sectionInitCount += 1;
    mountSlots();
    hydrateCompleteLookRails();
    disableExistingUpsellSections();
    storeViewedProduct();
    storeCollectionIntent();
    storeSearchIntent();
    syncCurrentVariant();
    renderRecentlyViewed();
    fetchProductRecommendations();
    recalcBundleTotals();
    updateReviewAnchors();
    initCartProgress();
  }

  function init() {
    bindListenersOnce();
    refresh();
  }

  window.JH.Recommendations = {
    version: '1.3.31.18AE14',
    __bootstrapped: true,
    init: init,
    refresh: refresh,
    addItems: addItems,
    getCart: getCart,
    renderRecentlyViewed: renderRecentlyViewed,
    fetchProductRecommendations: fetchProductRecommendations,
    hydrateCompleteLookRails: hydrateCompleteLookRails,
    inferIntent: inferIntent
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }



})();
